import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';

import { catchError, retry } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  AppPage,
  AppSettingsConfig,
  AppEmailSettings,
} from '../models/assets-data/AppSetttings.interface';
import { LocationConfig } from '../models/assets-data/LocationConfig.interface';
import {
  ReceivingLogForm,
  ReceivingLogFormConfig,
  MobilePrintSettings,
  PrintModeDefaults,
} from '../models/assets-data/receiving/ReceivingLogFormConfig.interface';
import {
  TechZonesConfig,
  TechZones,
} from '../models/assets-data/TechZonesConfig.interface';
import {
  ReceivingLogTableConfig,
  ReceivingLogTable,
} from '../models/assets-data/receiving/ReceivingLogTableConfig.interface';
import { Location } from '../models/assets-data/LocationConfig.interface';
import {
  UiConfig,
  Ui,
  ToastText,
  LoadingText,
} from '../models/assets-data/UiConfig.interface';

import { DEFAULT_ASSETS_APP_SETTINGS } from '../shared/constants/default-values/default-asset-values/app-data/default-assets-app-settings';
import { DEFAULT_ASSETS_LOCATION } from '../shared/constants/default-values/default-asset-values/app-data/default-assets-locations';
import { DEFAULT_ASSETS_RECEIVING_LOG_FORM } from '../shared/constants/default-values/default-asset-values/app-data/receiving/default-assets-receiving-log-form';
import { DEFAULT_ASSETS_RECEIVING_LOG_TABLE } from '../shared/constants/default-values/default-asset-values/app-data/receiving/default-assets-receiving-log-table';
import { DEFAULT_ASSETS_TECH_ZONES } from '../shared/constants/default-values/default-asset-values/app-data/default-assets-tech-zones';
import { DEFAULT_ASSETS_UI } from '../shared/constants/default-values/default-asset-values/app-data/default-assets-ui';

import { DEFAULT_SHEET_TEMPLATE } from '../shared/constants/default-values/default-asset-values/print-data/default-assets-large-label-template';
import { DEFAULT_SHEET_STYLES } from '../shared/constants/default-values/default-asset-values/print-data/default-assets-large-label-styles';
import { DEFAULT_QR_SHEET_TEMPLATE } from '../shared/constants/default-values/default-asset-values/print-data/default-assets-qr-large-label-template';
import { DEFAULT_QR_SHEET_STYLES } from '../shared/constants/default-values/default-asset-values/print-data/default-assets-qr-large-label-styles';
import { DEFAULT_DYMO_TEMPLATE } from '../shared/constants/default-values/default-asset-values/print-data/default-assets-dymo-template';
import { DEFAULT_DYMO_STYLES } from '../shared/constants/default-values/default-asset-values/print-data/default-assets-dymo-label-styles';
import { fetchAssetsToastErrors } from '../shared/constants/default-values/default-toast-message';

import { ToastService } from './toast.service';
import {
  LaydownLogForm,
  LaydownLogFormConfig,
} from '../models/assets-data/laydown/LaydownLogFormConfig.interface';
import {
  LaydownLogTable,
  LaydownLogTableConfig,
} from '../models/assets-data/laydown/LaydownLogTableConfig.interface';
import { DEFAULT_ASSETS_LAYDOWN_LOG_FORM } from '../shared/constants/default-values/default-asset-values/app-data/laydown-space/default-assets-laydown-log-form';
import { DEFAULT_ASSETS_LAYDOWN_LOG_TABLE } from '../shared/constants/default-values/default-asset-values/app-data/laydown-space/default-assets-laydown-log-table';

/**
 * @file assets-data.service.ts
 * @description This service is responsible for fetching and managing application assets, which include
 * various UI configurations, form settings, alert messages, button texts, and printing templates.
 * The fetched data is used throughout the application to ensure consistency in UI and settings.
 *
 *
 * The service provides methods to retrieve specific parts of the assets data, such as application
 * settings, locations, receiving log configurations, and text configurations for different UI components.
 * Additionally, it fetches print label templates for Dymo and large format labels.
 *
 * The methods GetAppDataAssets() and GetPrintLabelAssets() are called by the APP_INITIALIZER in app.module.ts
 * to ensure the assets are fetched before the app loads.
 *
 * !IMPORTANT NOTE!:
 * - The primary source of truth is the assets JSON files, which may change over time.
 * - If the HTTP request to fetch an asset fails, a default backup constant (with the same structure) is used.
 * - Initially, the backup data is identical to the asset files, but it does not receive updates when the asset files change.
 */

@Injectable({
  providedIn: 'root',
})
export class AssetsDataService {
  private _http: HttpClient;
  private _toastService: ToastService;

  public appSettingsData = new BehaviorSubject<AppSettingsConfig>(
    {} as AppSettingsConfig
  );
  public locationsData!: LocationConfig;
  public locations!: Location[];
  public receivingLogFormData!: ReceivingLogFormConfig;
  public receivingLogTableData!: ReceivingLogTableConfig;
  public laydownLogFormData!: LaydownLogFormConfig;
  public laydownLogTableData!: LaydownLogTableConfig;
  public techZonesData!: TechZonesConfig;
  public uiConfigData!: UiConfig;

  public assetToastGeneralWarning: string =
    fetchAssetsToastErrors['generalWarning'];

  public largeLabelHtmlTemplate!: string;
  public largeLabelCssStyles!: string;
  public qrLargeLabelHtmlTemplate!: string;
  public qrLargeLabelCssStyles!: string;
  public dymoLabelHTMLTemplate!: string;
  public dymoLabelCssStyles!: string;

  constructor(http: HttpClient, toastService: ToastService) {
    this._http = http;
    this._toastService = toastService;
  }

  //## Methods for retreiving all assets data, used by components in the app #########################################################

  //~~ Methods for retreiving app settings data ~~~~~~~~~~~~~~~~~~~~~~~~~

  /**
   * Returns an observable of the application pages data.
   */
  public GetAppPagesObservable(): Observable<AppSettingsConfig> {
    return this.appSettingsData.asObservable();
  }

  /**
   * Retrieves the list of application pages from app settings.
   */
  public GetAppPagesData(): AppPage[] {
    const appSettings = this.appSettingsData.value;
    return appSettings.appSettings.appPages;
  }

  /**
   * App-wide email addresses (expediting inbox, IT Service Desk + its CC). These are
   * location-independent, so they live in app-settings rather than per-location config.
   * Falls back to the in-code defaults if app-settings hasn't loaded (or predates the field).
   */
  public GetAppEmailSettings(): AppEmailSettings {
    return this.appSettingsData.value?.appSettings?.emails
      ?? DEFAULT_ASSETS_APP_SETTINGS.appSettings.emails;
  }

  /**
   * Retrieves the application name from app settings.
   */
  public GetAppName(): string {
    return this.appSettingsData.value.appSettings.appName;
  }

  /**
   * Retrieves the application icons from app settings.
   */
  public GetAppIconsData(): Array<string> {
    return this.appSettingsData.value.appSettings.appIcons;
  }

  //~~ Methods for retreiving locations data ~~~~~~~~~~~~~~~~~~~~~~~~~
  /**
   * Retrieves an array of Location data objects.
   */
  public GetLocationData(): Location[] {
    return this.locationsData.locations;
  }

  /**
   * Retrieves a list of location labels.
   */
  public GetLocationLabelsList(): string[] {
    return this.locationsData.locations.map(
      (location) => location.locationLabel
    );
  }

  /**
   * Retrieves a record of location names with their corresponding labels.
   */
  public GetLocationsRecord(): Record<string, string> {
    const locationObject: Record<string, string> = {};
    this.locationsData.locations.map((location) => {
      locationObject[location.locationName] = location.locationLabel;
    });
    return locationObject;
  }

  /**
   * Retrieves locations that have laydown space available.
   * @returns Array of Location objects where laydownSpaceAvailable is true
   * @private Internal helper method
   */
  private GetLaydownEnabledLocations(): Location[] {
    return this.locationsData.locations.filter(location => location.laydownSpaceAvailable);
  }

  /**
   * Retrieves a record of laydown-enabled location names with their labels.
   * @returns Record object with locationName as key and locationLabel as value
   */
  public GetLaydownEnabledLocationsRecord(): Record<string, string> {
    const laydownLocations = this.GetLaydownEnabledLocations();
    const locationsRecord: Record<string, string> = {};
    for (let i = 0; i < laydownLocations.length; i++) {
      locationsRecord[laydownLocations[i].locationName] = laydownLocations[i].locationLabel;
    }
    return locationsRecord;
  }

  /**
   * Checks if a specific location has laydown space available.
   * @param locationName - The name of the location to check
   * @returns true if laydown space is available, false otherwise
   */
  public IsLaydownAvailableAtLocation(locationName: string): boolean {
    const location = this.locationsData.locations.find(loc => loc.locationName === locationName);
    return location?.laydownSpaceAvailable ?? false;
  }

  //~~ Methods for retreiving the receiving log form data ~~~~~~~~~~~~~~~~~~~~~~~~~

  /**
   * Retrieves the receiving log form configuration.
   */
  public GetReceivingLogFormData(): ReceivingLogForm {
    return this.receivingLogFormData.receivingLogForm;
  }

  /**
   * Retrieves the mobile (Zebra) receiving flow print settings, falling back to
   * sensible defaults if the config predates the `mobile` section.
   */
  public GetMobilePrintSettings(): MobilePrintSettings {
    const fromConfig = this.receivingLogFormData?.receivingLogForm?.mobile;
    if (fromConfig) {
      return fromConfig;
    }
    return {
      printType: {
        label: 'Print Labels',
        options: [
          { value: 'zebra', label: 'Label' },
          { value: 'fullSheet', label: 'Copier Sheet' },
          { value: 'both', label: 'Both' },
          { value: 'none', label: 'No printing' },
        ],
      },
      defaults: {
        service: { printType: 'zebra', printQtyZebra: 1, printQtySheet: 1 },
        project: { printType: 'both', printQtyZebra: 1, printQtySheet: 1 },
      },
    };
  }

  /**
   * The mobile print method (browser vs backend auto-print), falling back to
   * mobile=auto for configs predating `printMode`. Desktop is not configurable —
   * it always prints through the browser (see print-path.rules.ts).
   */
  public GetPrintModeDefaults(): PrintModeDefaults {
    const fromConfig = this.receivingLogFormData?.receivingLogForm?.printMode;
    if (fromConfig) {
      return fromConfig;
    }
    return { mobile: 'auto' };
  }

  //~~ Methods for retreiving receiving log table data ~~~~~~~~~~~~~~~~~~~~~~~~~
  /**
   * Retrieves the receiving log table configuration.
   */
  public GetReceivingLogTableData(): ReceivingLogTable {
    return this.receivingLogTableData.receivingLogTable;
  }

  //~~ Methods for retreiving laydown log form data ~~~~~~~~~~~~~~~~~~~~~~~~~
  /**Retrieves the laydown log form configuration.
   */
  public GetLaydownLogFormData(): LaydownLogForm {
    return this.laydownLogFormData.laydownLogForm;
  }

  //~~ Methods for retreiving receiving log table data ~~~~~~~~~~~~~~~~~~~~~~~~~
  /**
   * Retrieves the receiving log table configuration.
   */
  public GetLaydownLogTableData(): LaydownLogTable {
    return this.laydownLogTableData.laydownLogTable;
  }

  //~~ Methods for retreiving tech zone data ~~~~~~~~~~~~~~~~~~~~~~~~~
  /**
   * Retrieves the tech zones configuration.
   */
  public GetTechZoneData(): TechZones {
    return this.techZonesData.techZones;
  }

  //~~ Methods for retreiving UI config data ~~~~~~~~~~~~~~~~~~~~~~~~~
  /**
   * Retrieves the UI configuration.
   */
  public GetUiConfigData(): Ui {
    return this.uiConfigData.uiConfig;
  }

  /**
   * Retrieves the toast alert text configuration.
   */
  public GetToastText(): ToastText {
    return this.uiConfigData.uiConfig.toastText;
  }

  /**
   * Retrieves the loading text configuration.
   */
  public GetLoadingText(): LoadingText {
    return this.uiConfigData.uiConfig.loadingText;
  }

  //~~ Methods for retreiving Printing label data ~~~~~~~~~~~~~~~~~~~~~~~~~

  // Method to retreive both HTML and CSS templates for the large format label.
  // `useQr` selects the QR-carrying template/styles (sheet/qr-large-label-*)
  // over the plain twin (sheet/large-label-*) — see PrintService.usesQrSheet
  // for when each applies.
  public GetFullSheetAssets(useQr: boolean = false): { html: string; css: string } {
    return useQr
      ? {
          html: this.qrLargeLabelHtmlTemplate,
          css: this.qrLargeLabelCssStyles,
        }
      : {
          html: this.largeLabelHtmlTemplate,
          css: this.largeLabelCssStyles,
        };
  }

  // Method to retreive both HTML and CSS templates for the dymo format label
  public GetDymoLabelAssets(): { html: string; css: string } {
    return {
      html: this.dymoLabelHTMLTemplate,
      css: this.dymoLabelCssStyles,
    };
  }

  //## Methods for fetching asset data (Calls HTTP request methods below) #########################################################

  /**
   * Fetches the application data assets by making an HTTP request and updates relevant data properties.
   *
   * This method is called by the APP_INITIALIZER in app.module.ts to ensure the json-app-data assets are fetched before the app loads.
   *
   * @returns {Promise<void>} A promise that resolves when the data is successfully retrieved.
   */
  public GetAppDataAssets(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.fetchAppDataAssets().subscribe({
        next: (data: any[]) => {
          this.appSettingsData.next(data[0]); // Update the BehaviorSubject with fetched data
          this.locationsData = data[1];
          this.locations = data[1]?.locations ?? []; // keep the flat accessor in sync with locationsData
          this.techZonesData = data[2];
          this.uiConfigData = data[3];
          this.receivingLogFormData = data[4];
          this.receivingLogTableData = data[5];
          this.laydownLogFormData = data[6];
          this.laydownLogTableData = data[7];
          resolve(); // Notify the APP_INITIALIZER that the initialization is complete
        },
        error: (error) => {
          console.error('Error fetching app data assets:', error);
          reject(error); // Notify the APP_INITIALIZER of the failure
        },
      });
    });
  }

  /**
   * Fetches the print label assets by making an HTTP request and updates label template properties.
   *
   * This method is called by the APP_INITIALIZER in app.module.ts to ensure the print-label-data assets are fetched before the app loads.
   *
   * @returns {Promise<void>} A promise that resolves when the data is successfully retrieved.
   */
  public GetPrintLabelAssets(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.fetchPrintTemplates().subscribe({
        next: (data: any[]) => {
          this.dymoLabelHTMLTemplate = data[0];
          this.dymoLabelCssStyles = data[1];
          this.largeLabelHtmlTemplate = data[2];
          this.largeLabelCssStyles = data[3];
          this.qrLargeLabelHtmlTemplate = data[4];
          this.qrLargeLabelCssStyles = data[5];

          resolve(); // Notify the APP_INITIALIZER that the initialization is complete
        },
        error: (error) => {
          console.error('Error fetching print label assets:', error);
          reject(error); // Notify the APP_INITIALIZER of the failure
        },
      });
    });
  }

  /**
   * Fetches the application data assets configuration files concurrently.
   * @returns {Observable<any[]>} An observable containing an array of fetched data.
   */
  private fetchAppDataAssets(): Observable<any[]> {
    return forkJoin([
      this.fetchAppSettings(),
      this.fetchLocationConfig(),
      this.fetchTechZonesConfig(),
      this.fetchUiConfig(),
      this.fetchReceivingLogFormConfig(),
      this.fetchReceivingLogTableConfig(),
      this.fetchLaydownLogFormConfig(),
      this.fetchLaydownLogTableConfig(),
    ]);
  }

  /**
   * Fetches the print label templates concurrently.
   * @returns {Observable<any[]>} An observable containing an array of fetched print templates.
   */
  private fetchPrintTemplates(): Observable<any[]> {
    return forkJoin([
      this.fetchDymoTemplate(),
      this.fetchDymoLabelStyles(),
      this.fetchLargeLabelTemplate(),
      this.fetchLargeLabelStyles(),
      this.fetchQrLargeLabelTemplate(),
      this.fetchQrLargeLabelStyles(),
    ]);
  }

  //## HTTP Requests for fetching app asset files (Requires Entra authentication) ############################################
  // If the asset files cannot be fetched, default backup objects (matching the same structure) are used.

  /**
   * Fetches the application settings configuration file.
   * @returns {Observable<AppSettingsConfig>} An observable containing the app settings data.
   * Falls back to a default constant if the request fails.
   */
  private fetchAppSettings(): Observable<AppSettingsConfig> {
    return this._http
      .get<AppSettingsConfig>('./assets/data/json-app-data/app-settings.json')
      .pipe(
        retry(3), // Retries 3 times before throwing an error
        catchError((error) => {
          console.error('Error fetching app settings:', error);
          const toastText = fetchAssetsToastErrors['appSettings'];
          this._toastService.showWarningMessage(
            toastText + ' ' + this.assetToastGeneralWarning
          );
          return of(DEFAULT_ASSETS_APP_SETTINGS);
        })
      );
  }

  /**
   * Fetches the location configuration file.
   * @returns {Observable<LocationConfig>} An observable containing the location configuration data.
   * Falls back to a default constant if the request fails.
   */
  private fetchLocationConfig(): Observable<LocationConfig> {
    return this._http
      .get<LocationConfig>('./assets/data/json-app-data/locations-config.json')
      .pipe(
        retry(3), // Retries 3 times before throwing an error
        catchError((error) => {
          console.error('Error fetching locations config:', error);
          const toastText = fetchAssetsToastErrors['locationConfig'];
          this._toastService.showWarningMessage(
            toastText + ' ' + this.assetToastGeneralWarning
          );
          return of(DEFAULT_ASSETS_LOCATION);
        })
      );
  }

  /**
   * Fetches the tech zones configuration file.
   * @returns {Observable<TechZonesConfig>} An observable containing the tech zones data.
   * Falls back to a default constant if the request fails.
   */
  private fetchTechZonesConfig(): Observable<TechZonesConfig> {
    return this._http
      .get<TechZonesConfig>(
        './assets/data/json-app-data/tech-zones-config.json'
      )
      .pipe(
        retry(3), // Retries 3 times before throwing an error
        catchError((error) => {
          console.error('Error fetching tech zones config:', error);
          const toastText = fetchAssetsToastErrors['techZoneConfig'];
          this._toastService.showWarningMessage(
            toastText + ' ' + this.assetToastGeneralWarning
          );
          return of(DEFAULT_ASSETS_TECH_ZONES);
        })
      );
  }

  /**
   * Fetches the UI configuration file.
   * @returns {Observable<UiConfig>} An observable containing the UI configuration data.
   * Falls back to a default constant if the request fails.
   */
  private fetchUiConfig(): Observable<UiConfig> {
    return this._http
      .get<UiConfig>('./assets/data/json-app-data/ui-config.json')
      .pipe(
        retry(3), // Retries 3 times before throwing an error
        catchError((error) => {
          console.error('Error fetching ui config:', error);
          const toastText = fetchAssetsToastErrors['uiConfig'];
          this._toastService.showWarningMessage(
            toastText + ' ' + this.assetToastGeneralWarning
          );
          return of(DEFAULT_ASSETS_UI);
        })
      );
  }

  /**
   * Makes an HTTP GET request to retreive the dymo label html template.
   * @returns {Observable<string[]>} An observable of the dymo label html template.
   * Falls back to a default constant if the request fails.
   */
  private fetchDymoTemplate(): Observable<any> {
    return this._http
      .get('./assets/data/print-labels-data/label/dymo-template.html', {
        responseType: 'text',
      })
      .pipe(
        retry(3),
        catchError((error) => {
          console.error('Error fetching dymo-template.html:', error);
          const toastText = fetchAssetsToastErrors['dymoTemplate'];
          this._toastService.showWarningMessage(
            toastText + ' ' + this.assetToastGeneralWarning
          );
          return of(DEFAULT_DYMO_TEMPLATE);
        })
      );
  }

  /**
   * Makes an HTTP GET request to retreive the dymo label css styles.
   * @returns {Observable<string[]>} An observable of the dymo label css styles.
   * Falls back to a default constant if the request fails.
   */
  private fetchDymoLabelStyles(): Observable<any> {
    return this._http
      .get('./assets/data/print-labels-data/label/dymo-label-styles.css', {
        responseType: 'text',
      })
      .pipe(
        retry(3),
        catchError((error) => {
          console.error('Error fetching dymo-label-styles.css:', error);
          const toastText = fetchAssetsToastErrors['dymoStyles'];
          this._toastService.showWarningMessage(
            toastText + ' ' + this.assetToastGeneralWarning
          );
          return of(DEFAULT_DYMO_STYLES);
        })
      );
  }

  /**
   * Fetches the large label HTML template file.
   * @returns {Observable<any>} An observable containing the large label template as a string.
   * Falls back to a default constant if the request fails.
   */
  private fetchLargeLabelTemplate(): Observable<any> {
    return this._http
      .get('./assets/data/print-labels-data/sheet/large-label-template.html', {
        responseType: 'text',
      })
      .pipe(
        catchError((error) => {
          console.error('Error fetching large-label-template.html:', error);
          const toastText = fetchAssetsToastErrors['sheetTemplate'];
          this._toastService.showWarningMessage(
            toastText + ' ' + this.assetToastGeneralWarning
          );
          return of(DEFAULT_SHEET_TEMPLATE);
        })
      );
  }

  /**
   * Fetches the large label styles CSS file.
   * @returns {Observable<any>} An observable containing the large label styles as a string.
   *  Falls back to a default constant if the request fails.
   */
  private fetchLargeLabelStyles(): Observable<any> {
    return this._http
      .get('./assets/data/print-labels-data/sheet/large-label-styles.css', {
        responseType: 'text',
      })
      .pipe(
        retry(3),
        catchError((error) => {
          console.error('Error fetching large-label-styles.css:', error);
          const toastText = fetchAssetsToastErrors['sheetStyles'];
          this._toastService.showWarningMessage(
            toastText + ' ' + this.assetToastGeneralWarning
          );
          return of(DEFAULT_SHEET_STYLES);
        })
      );
  }

  /**
   * Fetches the QR-carrying large label HTML template file — the twin of
   * large-label-template.html used whenever PrintService.usesQrSheet is true.
   * @returns {Observable<any>} An observable containing the QR large label template as a string.
   * Falls back to a default constant if the request fails.
   */
  private fetchQrLargeLabelTemplate(): Observable<any> {
    return this._http
      .get('./assets/data/print-labels-data/sheet/qr-large-label-template.html', {
        responseType: 'text',
      })
      .pipe(
        catchError((error) => {
          console.error('Error fetching qr-large-label-template.html:', error);
          const toastText = fetchAssetsToastErrors['qrSheetTemplate'];
          this._toastService.showWarningMessage(
            toastText + ' ' + this.assetToastGeneralWarning
          );
          return of(DEFAULT_QR_SHEET_TEMPLATE);
        })
      );
  }

  /**
   * Fetches the QR-carrying large label styles CSS file — the twin of
   * large-label-styles.css used whenever PrintService.usesQrSheet is true.
   * @returns {Observable<any>} An observable containing the QR large label styles as a string.
   *  Falls back to a default constant if the request fails.
   */
  private fetchQrLargeLabelStyles(): Observable<any> {
    return this._http
      .get('./assets/data/print-labels-data/sheet/qr-large-label-styles.css', {
        responseType: 'text',
      })
      .pipe(
        retry(3),
        catchError((error) => {
          console.error('Error fetching qr-large-label-styles.css:', error);
          const toastText = fetchAssetsToastErrors['qrSheetStyles'];
          this._toastService.showWarningMessage(
            toastText + ' ' + this.assetToastGeneralWarning
          );
          return of(DEFAULT_QR_SHEET_STYLES);
        })
      );
  }

 
  /**
   * Fetches the receiving log form configuration file.
   * @returns {Observable<ReceivingLogFormConfig>} An observable containing the receiving log form data.
   * Falls back to a default constant if the request fails.
   */
  private fetchReceivingLogFormConfig(): Observable<ReceivingLogFormConfig> {
    return this._http
      .get<ReceivingLogFormConfig>(
        './assets/data/json-app-data/receiving/receiving-log-form-config.json'
      )
      .pipe(
        retry(3), // Retries 3 times before throwing an error
        catchError((error) => {
          console.error('Error fetching receiving log form config:', error);
          const toastText = fetchAssetsToastErrors['logFormConfig'];
          this._toastService.showWarningMessage(
            toastText + ' ' + this.assetToastGeneralWarning
          );
          return of(DEFAULT_ASSETS_RECEIVING_LOG_FORM);
        })
      );
  }

  /**
   * Fetches the receiving log table configuration file.
   * @returns {Observable<ReceivingLogTableConfig>} An observable containing the receiving log table data.
   * Falls back to a default constant if the request fails.
   */
  private fetchReceivingLogTableConfig(): Observable<ReceivingLogTableConfig> {
    return this._http
      .get<ReceivingLogTableConfig>(
        './assets/data/json-app-data/receiving/receiving-log-table-config.json'
      )
      .pipe(
        retry(3), // Retries 3 times before throwing an error
        catchError((error) => {
          console.error('Error fetching receiving log table config:', error);
          const toastText = fetchAssetsToastErrors['logTableConfig'];
          this._toastService.showWarningMessage(
            toastText + ' ' + this.assetToastGeneralWarning
          );
          return of(DEFAULT_ASSETS_RECEIVING_LOG_TABLE);
        })
      );
  }

   /**
   * Fetches the laydown log form configuration file.
   * @returns {Observable<LaydownLogFormConfig>} An observable containing the laydown log form data.
   * Falls back to a default constant if the request fails.
   */
  private fetchLaydownLogFormConfig(): Observable<LaydownLogFormConfig> {
    return this._http
      .get<LaydownLogFormConfig>(
        './assets/data/json-app-data/laydown-space/laydown-log-form-config.json'
      )
      .pipe(
        retry(3), // Retries 3 times before throwing an error
        catchError((error) => {
          console.error('Error fetching laydown log form config:', error);
          const toastText = fetchAssetsToastErrors['logFormConfig'];
          this._toastService.showWarningMessage(
            toastText + ' ' + this.assetToastGeneralWarning
          );
          return of(DEFAULT_ASSETS_LAYDOWN_LOG_FORM);
        })
      );
  }

  /**
   * Fetches the laydown log table configuration file.
   * @returns {Observable<LaydownLogTableConfig>} An observable containing the laydown log table data.
   * Falls back to a default constant if the request fails.
   */
  private fetchLaydownLogTableConfig(): Observable<LaydownLogTableConfig> {
    return this._http
      .get<LaydownLogTableConfig>(
        './assets/data/json-app-data/laydown-space/laydown-log-table-config.json'
      )
      .pipe(
        retry(3), // Retries 3 times before throwing an error
        catchError((error) => {
          console.error('Error fetching laydown log table config:', error);
          const toastText = fetchAssetsToastErrors['logTableConfig'];
          this._toastService.showWarningMessage(
            toastText + ' ' + this.assetToastGeneralWarning
          );
          return of(DEFAULT_ASSETS_LAYDOWN_LOG_TABLE);
        })
      );
  }

}
